import { useEffect, useMemo, useState } from 'react';
import type { EventRow, Fixture } from '@/lens';
import { hydrate, lensFilterByRange, lensSelectRecentRuns } from '@/lens';
import { useSettings } from '@/data/settings';
import { fetchRecentRunEvents } from '@/data/firestore';

/**
 * 데이터 소스 — 원본 커스텀 액션 `lensLoadEvents` 의 짝.
 *
 * ⚠️⚠️ **순서가 전부다.** 회차 창 → 기간 필터 → 집계 → 배지.
 * 배지를 먼저 만들면 「21회차인데 완주율 100%」가 난다 — 배지가 분모와 **다른
 * 목록**을 세기 때문이다(실측).
 *
 * ★★ **창을 씌우는 자리가 소스마다 다르다.**
 * ```
 * mock       목업 생성 → lensSelectRecentRuns(여기서 창)
 * firestore  서버 쿼리가 **이미 창을 씌워** 온다 → 다시 걸지 않는다
 * ```
 * ⚠️ 원본 `lensLoadEvents` 가 정확히 그렇다. 두 번 걸면 쓰레기 필터가 두 번
 *    도는 셈이라 의미가 흐려진다.
 */

/** `dsl/edit.dart` 의 상수. ⚠️ 쓰레기 회차 필터가 **창을 세는 조건 안에** 있다. */
export const RUN_WINDOW = 100;
export const RUN_MIN_TURNS = 2;
/** `run_started` 를 몇 건까지 훑을지(서버 1단계). */
export const SCAN_LIMIT = 400;
export const SAMPLE_LIMIT = 5000;
/** `dsl/edit.dart` `_kStuckFloor` · `_kFlagFloorPct`. ★ 설정에 노출되지 않는다. */
export const STUCK_FLOOR = 3;
export const FLAG_FLOOR_PCT = 5;

export interface LensData {
  /** 소스가 준 전부. ★ 설정의 「소스 요약」은 **이것**을 본다(걸러진 수가 아니다). */
  raw: EventRow[];
  /** 회차 창까지. ★ 상세 화면이 본다(기간 필터를 안 거친다). */
  windowed: EventRow[];
  /** 창 + 기간 필터. ★ **집계는 전부 이것을 본다.** */
  rows: EventRow[];
  loading: boolean;
  error: string | null;
  /** 지금 실제로 읽은 소스. ⚠️ 화면이 **이것**을 배지에 쓴다. */
  mode: string;
  /**
   * ★ 임의 플레이(`casual_play`)로 **버린 회차 수**(개발 회신 39 §4).
   *
   * ⚠️ 지표에는 **안 들어간다** — 버렸다는 사실만 화면에 밝힌다.
   * ★ 조용히 버리면 「그런 회차가 없었다」와 구분이 안 된다 — 이 프로젝트에서
   *   반복해 나온 실패 유형이다.
   * ⚠️ 목업에는 그 칸이 없으므로 늘 0 이다.
   */
  casualRuns: number;
}

const EMPTY = { raw: [], windowed: [], rows: [], loading: true, error: null, mode: 'mock', casualRuns: 0 };

/**
 * 얼린 목업.
 *
 * ★★ **원본은 저장소 뿌리의 `fixtures/` 하나다** — 양쪽이 같은 바이트를 읽는
 * 것이 이식의 계약이다. `web/public/` 의 것은 `scripts/copy-fixture.mjs` 가
 * 매번 덮어쓰는 **사본**이고 추적하지 않는다(드리프트 위험이 없다).
 *
 * ⚠️ 예전에는 `new URL('../../../fixtures/...', import.meta.url)` 였는데
 * Vite 에서는 돌지만 **vitest(jsdom)에서 깨졌다**(`Denied ID` → `ENOENT`).
 */
const FIXTURE_URL = '/fixtures/lens_mock_events_v1.json';

async function loadMock(): Promise<EventRow[]> {
  const r = await fetch(FIXTURE_URL);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return hydrate((await r.json()) as Fixture);
}

export function useLensData(rangeKey: string): LensData {
  const { settings, reloadToken } = useSettings();
  const mode = settings.dataSourceMode;
  const excludedRunPrefixes = settings.excludedRunPrefixes;

  const [state, setState] = useState<{
    raw: EventRow[];
    windowed: EventRow[];
    loading: boolean;
    error: string | null;
    /**
     * ★ 임의 플레이로 **버린 회차 수**(개발 회신 39 §4).
     *
     * ⚠️ 지표에는 안 들어간다 — **버렸다는 사실만** 화면에 밝힌다.
     * ★ 조용히 버리면 「그런 회차가 없었다」와 구분이 안 된다.
     * ⚠️ 목업에는 `casual_play` 가 없으므로 늘 0 이다.
     */
    casualRuns: number;
  }>({ raw: [], windowed: [], loading: true, error: null, casualRuns: 0 });

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));

    const run = async () => {
      if (mode === 'mock') {
        const raw = await loadMock();
        // ★ 목업은 **여기서** 창을 씌운다. ⚠️ 목업에는 `casual_play` 가 없다.
        return { raw, windowed: lensSelectRecentRuns(raw, RUN_WINDOW, RUN_MIN_TURNS), casualRuns: 0 };
      }
      // ⚠️ 서버 쿼리가 이미 창을 씌웠다 — **다시 걸지 않는다.**
      const res = await fetchRecentRunEvents({
        runLimit: RUN_WINDOW,
        scanLimit: SCAN_LIMIT,
        minTurns: RUN_MIN_TURNS,
        excludedRunPrefixes,
      });
      return { raw: res.rows, windowed: res.rows, casualRuns: res.casualRuns };
    };

    run()
      .then(({ raw, windowed, casualRuns }) => {
        if (alive) setState({ raw, windowed, loading: false, error: null, casualRuns });
      })
      .catch((e: unknown) => {
        if (alive) {
          setState({
            raw: [],
            windowed: [],
            loading: false,
            error: e instanceof Error ? e.message : String(e),
            casualRuns: 0,
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [mode, excludedRunPrefixes, reloadToken]);

  return useMemo(() => {
    if (state.loading || state.error !== null) {
      return { ...EMPTY, loading: state.loading, error: state.error, mode };
    }
    return {
      raw: state.raw,
      windowed: state.windowed,
      rows: lensFilterByRange(state.windowed, rangeKey),
      loading: false,
      error: null,
      mode,
      casualRuns: state.casualRuns,
    };
  }, [state, rangeKey, mode]);
}
